import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const thisMonth = () => shopToday().slice(0, 7);

export default function Discounts() {
  const { branch, user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [allBranches, setAllBranches] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = ["admin", "it_admin"].includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/discounts", {
        params: { month, ...(allBranches && isAdmin ? {} : { branch }) },
      })
      .then(({ data }) => setData(data))
      .catch((err) =>
        toast.error(
          (err.response?.data?.detail && String(err.response.data.detail)) ||
            "Could not load the discounts report"
        )
      )
      .finally(() => setLoading(false));
  }, [month, allBranches, isAdmin, branch]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals;
  const rows = data?.rows || [];

  return (
    <div className="fade-up" data-testid="discounts-page">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
        {allBranches && isAdmin ? "All branches" : branch}
      </p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Discounts Given</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Every negotiated discount applied to an invoice — who approved it, how much came
        off and why. Keep an eye on how much is being given away.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-3" data-testid="discounts-filters">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Month
          </label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="mt-1 block h-10 w-[180px] rounded-none border border-border px-3 text-sm"
            data-testid="discounts-month"
          />
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2 px-1 pb-2.5 text-sm">
            <input
              type="checkbox"
              checked={allBranches}
              onChange={(e) => setAllBranches(e.target.checked)}
              data-testid="discounts-all-branches"
            />
            Both stores
          </label>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          ["Discounts given", t?.count ?? 0, "discounts-count"],
          ["Total given away (inc GST)", money(t?.discount_inc_gst ?? 0), "discounts-total-inc"],
          ["Total given away (ex GST)", money(t?.discount_ex_gst ?? 0), "discounts-total-ex"],
        ].map(([label, value, testid]) => (
          <div key={testid} className="border border-border p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-2 text-2xl font-bold" data-testid={testid}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto border border-border">
        <table className="w-full min-w-[900px] text-sm" data-testid="discounts-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Staff</th>
              <th className="px-3 py-2.5 text-left font-semibold">Invoice</th>
              <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
              <th className="px-3 py-2.5 text-right font-semibold">Was</th>
              <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
              <th className="px-3 py-2.5 text-right font-semibold">Discount</th>
              <th className="px-3 py-2.5 text-left font-semibold">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-12 text-center text-muted-foreground">
                  {loading ? "Loading…" : "No discounts given in this month."}
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-[#FBF8F2]" data-testid={`discount-row-${i}`}>
                <td className="tabular px-3 py-2.5 text-xs">
                  {r.date}
                  <div className="text-muted-foreground">{r.time}</div>
                </td>
                <td className="px-3 py-2.5">
                  {r.staff}
                  {allBranches && isAdmin && r.branch ? (
                    <div className="text-xs text-muted-foreground">{r.branch}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2.5 font-semibold">{r.doc_number ? `#${r.doc_number}` : "—"}</td>
                <td className="px-3 py-2.5">{r.customer_name || "—"}</td>
                <td className="tabular px-3 py-2.5 text-right text-muted-foreground">
                  {money(r.original_total)}
                </td>
                <td className="tabular px-3 py-2.5 text-right">{money(r.agreed_total)}</td>
                <td className="tabular px-3 py-2.5 text-right font-bold text-[#9A3412]">
                  -{money(r.discount_inc_gst)}
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
